import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { 
  Zap, 
  Sun, 
  Users, 
  TrendingUp, 
  ArrowRight,
  X,
  ChevronRight,
  ChevronLeft,
  Sparkles
} from 'lucide-react';

const TUTORIAL_STEPS = [
  {
    id: 1,
    title: "Bienvenue sur Green Teva ! 👋",
    description: "La plateforme de coopérative énergétique qui vous permet de partager l'énergie renouvelable avec vos voisins.",
    icon: Zap,
    color: "from-green-400 to-emerald-600"
  },
  {
    id: 2,
    title: "Producteur ou Consommateur ?",
    description: "Vous avez des panneaux solaires ? Vendez votre surplus ! Sinon, achetez de l'énergie locale moins chère que le réseau.",
    icon: Sun,
    color: "from-yellow-400 to-orange-500"
  },
  {
    id: 3,
    title: "Rejoignez une communauté",
    description: "Trouvez une communauté près de chez vous ou créez la vôtre. Échangez avec vos voisins dans un rayon de 2km.",
    icon: Users,
    color: "from-blue-400 to-cyan-500"
  },
  {
    id: 4,
    title: "Économisez automatiquement",
    description: "Notre algorithme répartit l'énergie au meilleur prix. Vous définissez vos limites, on s'occupe du reste !",
    icon: TrendingUp,
    color: "from-purple-400 to-pink-500"
  }
];

const Tutorial = ({ onComplete, isOpen }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const handleNext = () => {
    if (currentStep < TUTORIAL_STEPS.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      handleComplete();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('tutorialCompleted', 'true');
    onComplete();
  };

  const handleSkip = () => {
    localStorage.setItem('tutorialCompleted', 'true');
    onComplete();
  };

  if (!isOpen) return null;

  const step = TUTORIAL_STEPS[currentStep];
  const Icon = step.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          transition={{ type: "spring", duration: 0.5 }}
          className="relative bg-white rounded-3xl shadow-2xl max-w-md w-full overflow-hidden"
        >
          {/* Skip button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-2 text-muted-foreground hover:text-foreground transition-colors z-10"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Gradient header */}
          <div className={`bg-gradient-to-br ${step.color} p-8 text-white`}>
            <motion.div
              key={currentStep}
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ type: "spring", duration: 0.6 }}
              className="w-20 h-20 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4 backdrop-blur-sm"
            >
              <Icon className="w-10 h-10" />
            </motion.div>
            
            <motion.h2
              key={`title-${currentStep}`}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-2xl font-bold text-center"
            >
              {step.title}
            </motion.h2>
          </div>

          {/* Content */}
          <div className="p-8">
            <motion.p
              key={`desc-${currentStep}`}
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="text-center text-muted-foreground mb-8"
            >
              {step.description}
            </motion.p>

            {/* Progress dots */}
            <div className="flex justify-center gap-2 mb-8">
              {TUTORIAL_STEPS.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentStep 
                      ? 'w-8 bg-primary' 
                      : 'bg-muted hover:bg-muted-foreground'
                  }`}
                />
              ))}
            </div>

            {/* Navigation buttons */}
            <div className="flex gap-3">
              {currentStep > 0 && (
                <Button
                  variant="outline"
                  onClick={handlePrev}
                  className="flex-1"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Précédent
                </Button>
              )}
              
              <Button
                onClick={handleNext}
                className={`flex-1 btn-primary ${currentStep === 0 ? 'w-full' : ''}`}
              >
                {currentStep === TUTORIAL_STEPS.length - 1 ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Commencer
                  </>
                ) : (
                  <>
                    Suivant
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </>
                )}
              </Button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export const useTutorial = () => {
  const [showTutorial, setShowTutorial] = useState(false);

  useEffect(() => {
    const completed = localStorage.getItem('tutorialCompleted');
    if (!completed) {
      // Show tutorial after a short delay
      const timer = setTimeout(() => setShowTutorial(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const resetTutorial = () => {
    localStorage.removeItem('tutorialCompleted');
    setShowTutorial(true);
  };

  return {
    showTutorial,
    setShowTutorial,
    resetTutorial,
    TutorialComponent: () => (
      <Tutorial isOpen={showTutorial} onComplete={() => setShowTutorial(false)} />
    )
  };
};

export default Tutorial;
